import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useTheme} from '@mui/material';
import {green, red} from '@mui/material/colors';

/**
 * 引数
 */
interface Props {
  title: string;
  source: any;
  x: {
    dsColumn: string;
  };
  y: {
    dsColumn: string;
  };
  series: string;
  visual_map: {
    min: number;
    max: number;
    show?: boolean;
    inRangeColor?: string[];
  };
  grid?: {
    top?: number | string;
    bottom?: number | string;
    left?: number | string;
    right?: number | string;
  };
  onClick?: (prm: {x: string; y: string}) => void;
  exportData?: any;
  exportFields?: any;
  exportFilename?: string;
  graphic?: any;
  selected?: any;
  //heightは自動調整されるので追加しないこと
}

/**
 * LFCChartsHeatMap
 * @param props
 * @returns
 */
const LFCChartsHeatMap = React.memo(
  ({
    title,
    source,
    x,
    y,
    series,
    visual_map,
    grid,
    onClick,
    exportData,
    exportFields,
    exportFilename,
    graphic,
    selected
  }: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const [dataSeriesX, setDataSeriesX] = useState([]);
    const [dataSeriesY, setDataSeriesY] = useState([]);
    const [dataPlot, setDataPlot] = useState([]);
    const [hhh, setHHH] = useState('');
    /**
     * sourceからXとY軸を作成
     */
    useEffect(() => {
      // シリーズ抽出：ヒートマップはXY全てCategory。
      let xds: any = [];
      let yds: any = [];
      let dp: any = [];

      if (source.length > 0) {
        // 軸毎のデータを取る
        xds = Array.from(new Set(source.map((value: any) => value[x.dsColumn]))).sort(
          (a, b) => Number(a) - Number(b)
        );
        yds = Array.from(new Set(source.map((value: any) => value[y.dsColumn]))).sort();

        if (xds.indexOf('None') !== -1) {
          xds.splice(xds.indexOf('None'), 1);
          xds.push('None');
        }
        // データソースからシリーズのインデックス配列(ヒートマップの座標に)変換
        source.forEach((item: any) => {
          dp.push([xds.indexOf(item[x.dsColumn]), yds.indexOf(item[y.dsColumn]), item[series]]);
        });
      }

      setDataSeriesX(xds);
      setDataSeriesY(yds);
      setDataPlot(dp);

      setHHH(`${String(150 + yds.length * 20)}px`); //チャートの状況で高さを動的に変更
    }, [source, x.dsColumn, y.dsColumn, series]);

    const makeLineData = () => {
      if (selected !== undefined && selected.y !== '') {
        return {
          data: [
            {
              yAxis: selected.y
            }
          ],
          label: {
            formatter: t('選択中')
          }
        };
      } else {
        return undefined;
      }
    };

    const chartOption = {
      title: {
        text: title
      },
      grid: grid
        ? grid
        : {
            top: '40',
            left: '130',
            right: '10',
            bottom: 100
          },
      legend: {type: 'scroll', bottom: 0, itemWidth: 10},
      tooltip: false,
      xAxis: {
        type: 'category',
        data: dataSeriesX,
        axisLabel: {
          rotate: '45',
          fontSize: 11,
          width: 100,
          overflow: 'truncate'
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.divider
          }
        }
      },
      yAxis: {
        type: 'category',
        data: dataSeriesY,
        axisLabel: {
          interval: 0,
          width: 125,
          overflow: 'truncate'
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.divider
          }
        }
      },
      series: [
        {
          type: 'heatmap',
          data: dataPlot,
          selectedMode: 'single',
          markLine: makeLineData()
        }
      ],
      visualMap: {
        show: visual_map.show ? true : visual_map.show,
        min: visual_map.min,
        max: visual_map.max,
        inRange: {
          color: visual_map.inRangeColor ? visual_map.inRangeColor : [green[500], red[500]]
        },
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '0%'
      }
    };

    const chartEvents = {
      click: (params: any) => {
        if (onClick != null) {
          onClick({x: dataSeriesX[params.data[0]], y: dataSeriesY[params.data[1]]});
        }
      }
    };

    return (
      <LFCBaseCharts
        option={chartOption}
        onEvents={chartEvents}
        exportData={exportData}
        exportFields={exportFields}
        exportFilename={exportFilename}
        height={hhh}
      />
    );
  },
  (prevProps, nextProps) => {
    // 必要な項目が変化した時のみ再描画
    return (
      prevProps.source === nextProps.source &&
      prevProps.selected?.y === nextProps.selected?.y &&
      prevProps.title === nextProps.title &&
      prevProps.x.dsColumn === nextProps.x.dsColumn &&
      prevProps.y.dsColumn === nextProps.y.dsColumn &&
      prevProps.series === nextProps.series &&
      prevProps.visual_map.min === nextProps.visual_map.min &&
      prevProps.visual_map.max === nextProps.visual_map.max &&
      prevProps.visual_map.show === nextProps.visual_map.show
    );
  }
);
export default LFCChartsHeatMap;
